import { PageId } from "../types/pageId";
import { PricePresentationType } from "../types/prices";

interface PriceDisplayGroupBasedDisplayStrategy {
  eachGroupStrategy: PricePresentationType;
  rangeGroupStrategy: PricePresentationType;
  rangeGroupWithQuantityStrategy: PricePresentationType;
  fromGroupStrategy: PricePresentationType;
  fallbackStrategy?: PricePresentationType;
}

export enum PriceDisplayVersion {
  Legacy = "legacy",
  Current = "current",
}
export interface UnifiedDisplayStrategy {
  [PriceDisplayVersion.Legacy]: PriceDisplayGroupBasedDisplayStrategy;
  [PriceDisplayVersion.Current]: PriceDisplayGroupBasedDisplayStrategy;
}

export type PageIdDisplayStrategyMap = Record<
  PageId, UnifiedDisplayStrategy
>;

type DisplayStrategyMap = Partial<Record<PageId, UnifiedDisplayStrategy>>;

const baseStrategies = {
  qtyStartingAt: {
    eachGroupStrategy: PricePresentationType.QtyStartingAtPrice,
    fromGroupStrategy: PricePresentationType.QtyStartingAtPrice,
    rangeGroupStrategy: PricePresentationType.QtyStartingAtPrice,
    rangeGroupWithQuantityStrategy: PricePresentationType.QtyStartingAtPrice,
  },
  qtyFrom: {
    eachGroupStrategy: PricePresentationType.QtyFromPrice,
    fromGroupStrategy: PricePresentationType.QtyFromPrice,
    rangeGroupStrategy: PricePresentationType.QtyFromPrice,
    rangeGroupWithQuantityStrategy: PricePresentationType.QtyFromPrice,
  },
  qtyFor: {
    eachGroupStrategy: PricePresentationType.QtyForPrice,
    fromGroupStrategy: PricePresentationType.QtyForPrice,
    rangeGroupStrategy: PricePresentationType.QtyForPrice,
    rangeGroupWithQuantityStrategy: PricePresentationType.QtyForPrice,
  },
  rawPrice: {
    eachGroupStrategy: PricePresentationType.RawPrice,
    fromGroupStrategy: PricePresentationType.RawPrice,
    rangeGroupStrategy: PricePresentationType.RawPrice,
    rangeGroupWithQuantityStrategy: PricePresentationType.RawPrice,
  },
  grouped: {
    eachGroupStrategy: PricePresentationType.UnitPriceInfo,
    rangeGroupStrategy: PricePresentationType.UnitRangePrice,
    rangeGroupWithQuantityStrategy: PricePresentationType.DetailedPriceInfo,
    fromGroupStrategy: PricePresentationType.PriceInfo,
  },
  priceInfo: {
    eachGroupStrategy: PricePresentationType.PriceInfo,
    fromGroupStrategy: PricePresentationType.PriceInfo,
    rangeGroupStrategy: PricePresentationType.PriceInfo,
    rangeGroupWithQuantityStrategy: PricePresentationType.PriceInfo,
  },
};

export const DisplayStrategyConfig: DisplayStrategyMap = {
  [PageId.PDP]: {
    [PriceDisplayVersion.Legacy]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceWithQtyAndUnit,

    },
    [PriceDisplayVersion.Current]: baseStrategies.grouped,
  },
  [PageId.CorePDP]: {
    [PriceDisplayVersion.Legacy]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceWithQtyAndUnit,
    },
    [PriceDisplayVersion.Current]: baseStrategies.grouped,
  },
  [PageId.LatPDP]: {
    [PriceDisplayVersion.Legacy]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceWithQtyAndUnit,
    },
    [PriceDisplayVersion.Current]: baseStrategies.grouped,
  },
  [PageId.Gallery]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyFrom,
    [PriceDisplayVersion.Current]: baseStrategies.qtyFor,
  },
  [PageId.ChecksQuickView]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.PDC]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.rawPrice,
    [PriceDisplayVersion.Current]: baseStrategies.priceInfo,
  },
  [PageId.Cart]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.rawPrice,
    [PriceDisplayVersion.Current]: baseStrategies.rawPrice,
  },
  [PageId.Category]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.HomePage]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.SearchPages]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.SearchDesignTiles]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,

  },
  [PageId.PLP]: {
    [PriceDisplayVersion.Legacy]: {
      rangeGroupStrategy: PricePresentationType.RangePrice,
      fromGroupStrategy: PricePresentationType.RangePrice,
      eachGroupStrategy: PricePresentationType.RangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.RangePrice,
    },
    [PriceDisplayVersion.Current]: {
      rangeGroupStrategy: PricePresentationType.RangePrice,
      fromGroupStrategy: PricePresentationType.RangePrice,
      eachGroupStrategy: PricePresentationType.RangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.RangePrice,
    },
  },
  [PageId.GalleryQuickView]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyFrom,
    [PriceDisplayVersion.Current]: baseStrategies.qtyFor,
  },
  [PageId.Favorites]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.LaunchMyBusiness]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.QuickEditor]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.Checkout]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.OrderConfirmation]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.QuantitySelectionPage]: {
    [PriceDisplayVersion.Legacy]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceWithQtyAndUnit,
    },
    [PriceDisplayVersion.Current]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.DetailedPriceInfo,
    },
  },
  [PageId.QuantitySelectionPageTiles]: {
    [PriceDisplayVersion.Legacy]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceWithQtyAndUnit,
    },
    [PriceDisplayVersion.Current]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePriceForTile,
      rangeGroupWithQuantityStrategy: PricePresentationType.DetailedPriceInfo,
    },
  },
  [PageId.Discovery]: {
    [PriceDisplayVersion.Legacy]: {
      eachGroupStrategy: PricePresentationType.RangePrice,
      fromGroupStrategy: PricePresentationType.RangePrice,
      rangeGroupStrategy: PricePresentationType.RangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.RangePrice,
    },
    [PriceDisplayVersion.Current]: {
      eachGroupStrategy: PricePresentationType.RangePrice,
      fromGroupStrategy: PricePresentationType.RangePrice,
      rangeGroupStrategy: PricePresentationType.RangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.RangePrice,
    },
  },
  [PageId.RecommendationLane]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.RecommendationLaneWithQuantity]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
  [PageId.PpagProductTile]: {
    [PriceDisplayVersion.Legacy]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceForQtyUnits,
    },
    [PriceDisplayVersion.Current]: {
      ...baseStrategies.qtyStartingAt,
      rangeGroupStrategy: PricePresentationType.UnitRangePrice,
      rangeGroupWithQuantityStrategy: PricePresentationType.DetailedPriceInfo,
    },
  },
  //TODO: re-evaluate the strategy for team details page
  [PageId.TeamDetailsPage]: {
    [PriceDisplayVersion.Legacy]: {
      rangeGroupStrategy: PricePresentationType.PriceForQtyUnits,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceForQtyUnits,
      eachGroupStrategy: PricePresentationType.PriceForQtyUnits,
      fromGroupStrategy: PricePresentationType.PriceForQtyUnits,
    },
    [PriceDisplayVersion.Current]: {
      rangeGroupStrategy: PricePresentationType.PriceForQtyUnits,
      rangeGroupWithQuantityStrategy: PricePresentationType.PriceForQtyUnits,
      eachGroupStrategy: PricePresentationType.PriceForQtyUnits,
      fromGroupStrategy: PricePresentationType.PriceForQtyUnits,
    },
  },
  //Shown for mailer products so it can use priceInfo as display strategy
  [PageId.QuickProductFlowFlyout]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.priceInfo,
  },
  [PageId.FavoritesTemplateSection]: {
    [PriceDisplayVersion.Legacy]: baseStrategies.qtyStartingAt,
    [PriceDisplayVersion.Current]: baseStrategies.qtyStartingAt,
  },
};
